import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, UploadCloud, CheckCircle2, XCircle, Clock, Eye, Lock, ChevronRight, ChevronLeft } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api, { fmtErr, fmtDateTime } from "../../lib/api";
import { toast } from "sonner";

const DOCS = [
  { key: "gst_doc", label: "GST Certificate / Company Incorporation" },
  { key: "photo_doc", label: "Photo or Selfie" },
  { key: "pan_doc", label: "PAN Card / Driving License" },
  { key: "aadhaar_doc", label: "Aadhaar / Passport / Voter ID" },
];
const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-shipany-navy placeholder:text-slate-400 focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";

function Stepper({ current }) {
  const steps = ["Signup", "Profile & Bank", "Documents", "Approval"];
  return (
    <div className="mt-6 flex items-center" data-testid="kyc-stepper">
      {steps.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        return (
          <div key={label} className="flex flex-1 items-center last:flex-none">
            <div className="flex flex-col items-center">
              <span className={`flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-bold ${done ? "border-emerald-500 bg-emerald-500 text-white" : active ? "border-shipany-blue bg-shipany-blue text-white glow-blue" : "border-slate-300 bg-white text-slate-400"}`}>
                {done ? <CheckCircle2 className="h-5 w-5" /> : n}
              </span>
              <span className={`mt-1.5 whitespace-nowrap text-[10px] font-semibold sm:text-xs ${active ? "text-shipany-blue" : done ? "text-emerald-600" : "text-slate-400"}`}>{label}</span>
            </div>
            {n < steps.length && <div className={`mx-2 mb-5 h-0.5 flex-1 ${n < current ? "bg-emerald-500" : "bg-slate-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function Kyc() {
  const { checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ pan: "", gst: "", aadhaar: "", address: "", city: "", state: "", pincode: "", bank_holder: "", bank_account: "", bank_ifsc: "" });
  const [files, setFiles] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/kyc/status").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const viewDoc = async (fileId) => {
    try {
      const res = await api.get(`/kyc/docs/${fileId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const nextStep = () => {
    const required = ["pan", "aadhaar", "address", "city", "state", "pincode", "bank_holder", "bank_account", "bank_ifsc"];
    for (const k of required) {
      if (!String(form[k]).trim()) { toast.error("Please fill all profile, address and bank fields"); return; }
    }
    if (form.pan.trim().length !== 10) { toast.error("PAN must be 10 characters"); return; }
    if (form.pincode.trim().length !== 6) { toast.error("Pincode must be 6 digits"); return; }
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const d of DOCS) {
      if (!files[d.key]) { toast.error(`${d.label} is required`); return; }
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      DOCS.forEach((d) => fd.append(d.key, files[d.key]));
      await api.post("/kyc/submit", fd);
      toast.success("KYC submitted — our team will review it shortly");
      load();
    } catch (e2) { toast.error(fmtErr(e2)); } finally { setSaving(false); }
  };

  const status = data?.kyc_status || "not_submitted";
  const sub = data?.submission;
  const done = status === "approved" || status === "pending";
  const stepperCurrent = status === "approved" ? 5 : status === "pending" ? 4 : step === 2 ? 3 : 2;

  return (
    <div className="mx-auto max-w-3xl" data-testid="kyc-page">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-shipany-blue/10"><ShieldCheck className="h-5 w-5 text-shipany-blue" /></span>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Complete your KYC</h1>
          <p className="text-sm text-slate-500">Wallet recharge, shipment booking and all features unlock after admin approval.</p>
        </div>
      </div>

      <Stepper current={stepperCurrent} />

      {status === "approved" && (
        <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="kyc-approved-banner">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
          <p className="mt-2 font-display text-lg font-bold text-emerald-700">KYC Approved — you're all set!</p>
          <p className="text-xs text-emerald-600">Your account is fully activated for live shipping.</p>
          <button onClick={async () => { await checkAuth(); window.location.href = "/app"; }} data-testid="kyc-continue-btn"
            className="mt-4 rounded-xl bg-emerald-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-emerald-700">
            Start Shipping <ChevronRight className="inline h-4 w-4" />
          </button>
        </div>
      )}
      {status === "pending" && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="kyc-pending-banner">
          <Clock className="mx-auto h-10 w-10 text-amber-500" />
          <p className="mt-2 font-display text-lg font-bold text-amber-700">Waiting for approval</p>
          <p className="text-xs text-amber-600">Submitted {sub ? fmtDateTime(sub.submitted_at) : ""} — typically reviewed within 24 hours. We'll unlock recharge & booking the moment you're approved.</p>
        </div>
      )}
      {status === "rejected" && (
        <div className="mt-6 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 p-5" data-testid="kyc-rejected-banner">
          <XCircle className="h-6 w-6 shrink-0 text-red-500" />
          <div>
            <p className="font-bold text-red-600">KYC rejected — please correct and resubmit</p>
            <p className="text-xs text-red-500">Reason: {sub?.reason || "—"}</p>
          </div>
        </div>
      )}

      {sub && done && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="kyc-submission-summary">
          <div className="grid gap-3 text-sm sm:grid-cols-2">
            <div><p className="text-xs text-slate-400">PAN</p><p className="mono font-bold text-shipany-navy">{sub.pan}</p></div>
            <div><p className="text-xs text-slate-400">GSTIN</p><p className="mono font-bold text-shipany-navy">{sub.gst || "—"}</p></div>
            <div><p className="text-xs text-slate-400">Aadhaar / ID</p><p className="mono font-bold text-shipany-navy">{"••••" + (sub.aadhaar || "").slice(-4)}</p></div>
            <div><p className="text-xs text-slate-400">Bank</p><p className="mono font-bold text-shipany-navy">{sub.bank?.holder} · {sub.bank?.ifsc}</p></div>
            <div className="sm:col-span-2"><p className="text-xs text-slate-400">Address</p><p className="font-semibold text-shipany-navy">{sub.address?.line1}, {sub.address?.city}, {sub.address?.state} — {sub.address?.pincode}</p></div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {DOCS.map((d) => sub.documents?.[d.key] && (
              <button key={d.key} onClick={() => viewDoc(sub.documents[d.key].file_id)} data-testid={`kyc-view-${d.key}`}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-shipany-blue hover:bg-shipany-blue/5">
                <Eye className="h-3 w-3" /> {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {!done && (
        <form onSubmit={submit} className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="kyc-form">
          {step === 1 && (
            <div className="space-y-4" data-testid="kyc-step-1">
              <h2 className="font-display flex items-center gap-2 text-base font-bold text-shipany-navy"><Lock className="h-4 w-4 text-shipany-blue" /> Step 1 — Profile, Address & Bank</h2>
              <div className="grid gap-3.5 sm:grid-cols-2">
                <div><label className="text-xs font-semibold text-slate-500">PAN *</label>
                  <input data-testid="kyc-pan" className={`${inputCls} mono mt-1 uppercase`} maxLength={10} value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" /></div>
                <div><label className="text-xs font-semibold text-slate-500">GSTIN (optional for individuals)</label>
                  <input data-testid="kyc-gst" className={`${inputCls} mono mt-1 uppercase`} maxLength={15} value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value.toUpperCase() })} placeholder="06AABCA1234F1Z5" /></div>
                <div><label className="text-xs font-semibold text-slate-500">Aadhaar / ID Number *</label>
                  <input data-testid="kyc-aadhaar" className={`${inputCls} mono mt-1`} maxLength={16} value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value.replace(/\D/g, "") })} placeholder="Aadhaar / Passport / Voter ID number" /></div>
                <div><label className="text-xs font-semibold text-slate-500">Pincode *</label>
                  <input data-testid="kyc-pincode" className={`${inputCls} mono mt-1`} maxLength={6} value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "") })} placeholder="122001" /></div>
                <div className="sm:col-span-2"><label className="text-xs font-semibold text-slate-500">Business Address *</label>
                  <input data-testid="kyc-address" className={`${inputCls} mt-1`} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Shop/plot no, street, area" /></div>
                <div><label className="text-xs font-semibold text-slate-500">City *</label>
                  <input data-testid="kyc-city" className={`${inputCls} mt-1`} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Gurugram" /></div>
                <div><label className="text-xs font-semibold text-slate-500">State *</label>
                  <input data-testid="kyc-state" className={`${inputCls} mt-1`} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Haryana" /></div>
                <div><label className="text-xs font-semibold text-slate-500">Account Holder Name *</label>
                  <input data-testid="kyc-bank-holder" className={`${inputCls} mt-1`} value={form.bank_holder} onChange={(e) => setForm({ ...form, bank_holder: e.target.value })} placeholder="As per bank records" /></div>
                <div><label className="text-xs font-semibold text-slate-500">Bank Account Number *</label>
                  <input data-testid="kyc-bank-account" className={`${inputCls} mono mt-1`} value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value.replace(/\D/g, "") })} placeholder="For COD remittance" /></div>
                <div><label className="text-xs font-semibold text-slate-500">IFSC *</label>
                  <input data-testid="kyc-bank-ifsc" className={`${inputCls} mono mt-1 uppercase`} maxLength={11} value={form.bank_ifsc} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase() })} placeholder="HDFC0001234" /></div>
              </div>
              <button type="button" onClick={nextStep} data-testid="kyc-next-btn"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">
                Continue to Documents <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="kyc-step-2">
              <h2 className="font-display text-base font-bold text-shipany-navy">Step 2 — Upload Documents</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {DOCS.map((d) => (
                  <label key={d.key} data-testid={`kyc-upload-${d.key}`}
                    className={`flex cursor-pointer items-center gap-2.5 rounded-xl border border-dashed px-4 py-3.5 text-sm transition-colors ${files[d.key] ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 text-slate-500 hover:border-shipany-blue hover:text-shipany-blue"}`}>
                    {files[d.key] ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <UploadCloud className="h-4 w-4 shrink-0" />}
                    <span className="truncate text-xs font-semibold">{files[d.key] ? files[d.key].name : `${d.label} *`}</span>
                    <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden"
                      onChange={(e) => setFiles({ ...files, [d.key]: e.target.files[0] || null })} />
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-400">JPG, PNG, WEBP or PDF · max 5MB each · encrypted at rest, visible only to you and our compliance team.</p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(1)} data-testid="kyc-back-btn"
                  className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-5 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50">
                  <ChevronLeft className="h-4 w-4" /> Back
                </button>
                <button disabled={saving} data-testid="kyc-submit-btn"
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-60">
                  <ShieldCheck className="h-4 w-4" /> {saving ? "Uploading…" : "Submit for Approval"}
                </button>
              </div>
            </div>
          )}
        </form>
      )}
    </div>
  );
}
